import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { isSuperAdmin } from '../utils/roleUtils';
import { fetchAccessibleOrganizations } from '../utils/orgDataUtils';
import reportingService from '../services/reportingService';

const ReportingDashboard = () => {
  const { 
    userOrganizations,
    userRole,
    getReportingData, 
    refreshReportingData 
  } = useAuth();
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizationNames, setOrganizationNames] = useState({});
  const [allOrganizationIds, setAllOrganizationIds] = useState([]);
  const [overviewData, setOverviewData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Date range state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateReportData, setDateReportData] = useState(null);
  const [runningReport, setRunningReport] = useState(false);
  
  // Load organization names and IDs via admin API (avoids unscoped Firestore reads)
  const loadOrganizationNames = useCallback(async () => {
    try {
      const orgs = await fetchAccessibleOrganizations(userRole, userOrganizations || []);
      const names = {};
      const orgIds = [];

      orgs.forEach((org) => {
        names[org.id] = org.name || org.organizationName || org.title || 'Unknown Organization';
        orgIds.push(org.id);
      });

      setOrganizationNames(names);
      setAllOrganizationIds(orgIds);
    } catch (error) {
      console.error('Could not load organization names:', error.message);
      const fallbackNames = {};
      const fallbackIds = userOrganizations || [];
      fallbackIds.forEach((orgId) => {
        fallbackNames[orgId] = orgId;
      });
      setOrganizationNames(fallbackNames);
      setAllOrganizationIds(fallbackIds);
    }
  }, [userRole, userOrganizations]);

  // Load cached data for selected organization
  const loadCachedData = useCallback((organizationId) => {
    if (!organizationId) return;
    
    const data = getReportingData(organizationId);
    if (data) {
      setOverviewData(data);
      setError(null);
    } else {
      setError('No cached data available. Please refresh.');
    }
  }, [getReportingData]);

  // Handle organization change
  const handleOrganizationChange = useCallback((organizationId) => {
    setSelectedOrganization(organizationId);
    setOverviewData(null);
    setError(null);
    setDateReportData(null);
    loadCachedData(organizationId);
  }, [loadCachedData]);

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    if (!selectedOrganization) return;
    
    setRefreshing(true);
    setError(null);
    
    try {
      const freshData = await refreshReportingData(selectedOrganization);
      setOverviewData(freshData);
      console.log('Data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data. Please try again.');
    } finally {
      setRefreshing(false);
    }
  }, [selectedOrganization, refreshReportingData]);

  // Run date-based report
  const runDateReport = useCallback(async () => {
    if (!selectedOrganization || !startDate || !endDate) {
      setError('Please select an organization and date range');
      return;
    }

    setRunningReport(true);
    setError(null);
    
    try {
      const startTimestamp = new Date(startDate);
      const endTimestamp = new Date(endDate);
      endTimestamp.setHours(23, 59, 59, 999); // End of day
      
      console.log('Running date report for:', {
        organizationId: selectedOrganization,
        startDate: startTimestamp,
        endDate: endTimestamp
      });
      
      // Initialize counters
      const reportData = {
        courses: { accessed: 0, completed: 0 },
        levels: { accessed: 0, completed: 0 },
        lessons: { accessed: 0, completed: 0 },
        tasks: { accessed: 0, completed: 0 },
        prompts: { accessed: 0, completed: 0 },
        newUsers: 0,
        logins: 0
      };
      
      // Query 1: Get new users from users collection
      try {
        const usersRef = collection(db, 'users');
        const usersQuery = query(
          usersRef,
          where('organizationIds', 'array-contains', selectedOrganization),
          where('created_time', '>=', startTimestamp),
          where('created_time', '<=', endTimestamp)
        );
        const usersSnapshot = await getDocs(usersQuery);
        reportData.newUsers = usersSnapshot.size;
        console.log(`Found ${reportData.newUsers} new users in date range`);
      } catch (error) {
        console.warn('Could not query users collection for new users:', error);
        // Fallback: try without date filtering
        try {
          const usersRef = collection(db, 'users');
          const usersQuery = query(
            usersRef,
            where('organizationIds', 'array-contains', selectedOrganization)
          );
          const usersSnapshot = await getDocs(usersQuery);
          // Filter client-side for date range
          let newUserCount = 0;
          usersSnapshot.forEach(doc => {
            const userData = doc.data();
            if (userData.created_time) {
              const userCreatedTime = userData.created_time.toDate ? 
                userData.created_time.toDate() : 
                new Date(userData.created_time);
              if (userCreatedTime >= startTimestamp && userCreatedTime <= endTimestamp) {
                newUserCount++;
              }
            }
          });
          reportData.newUsers = newUserCount;
          console.log(`Found ${reportData.newUsers} new users (client-side filtered)`);
        } catch (fallbackError) {
          console.warn('Fallback users query also failed:', fallbackError);
          reportData.newUsers = 0;
        }
      }
      
      // Query analytics events for content interactions and logins (server-side)
      const { events = [] } = await reportingService.getDetailedEvents(
        selectedOrganization,
        startDate,
        endDate
      );

      events.forEach((data) => {
        const eventType = data.eventType;

        switch (eventType) {
          case 'course_accessed':
            reportData.courses.accessed++;
            break;
          case 'course_completed':
            reportData.courses.completed++;
            break;
          case 'level_accessed':
            reportData.levels.accessed++;
            break;
          case 'level_completed':
            reportData.levels.completed++;
            break;
          case 'lesson_accessed':
            reportData.lessons.accessed++;
            break;
          case 'lesson_completed':
            reportData.lessons.completed++;
            break;
          case 'task_completed':
            reportData.tasks.completed++;
            break;
          case 'prompt_accessed':
            reportData.prompts.accessed++;
            break;
          case 'prompt_completed':
            reportData.prompts.completed++;
            break;
          case 'user_login':
          case 'login':
          case 'user_signin':
          case 'signin':
          case 'session_start':
          case 'session_created':
          case 'auth_login':
            reportData.logins++;
            break;
          case 'logout':
          case 'user_logout':
          case 'signout':
          case 'session_end':
            break;
          default:
            break;
        }
      });

      setDateReportData(reportData);
      
    } catch (error) {
      console.error('Error running date report:', error);
      setError('Failed to generate date report. Please try again.');
    } finally {
      setRunningReport(false);
    }
  }, [selectedOrganization, startDate, endDate]);

  // Calculate content interactions total
  const getContentTotal = useCallback(() => {
    if (!dateReportData) return 0;
    return dateReportData.courses.accessed + 
           dateReportData.lessons.accessed + 
           dateReportData.tasks.completed + 
           dateReportData.prompts.accessed;
  }, [dateReportData]);

  // Calculate human interactions total
  const getHumanTotal = useCallback(() => {
    if (!dateReportData) return 0;
    return dateReportData.newUsers + dateReportData.logins;
  }, [dateReportData]);

  // Determine which organizations to show based on role
  const availableOrganizations = isSuperAdmin(userRole) ? allOrganizationIds : (userOrganizations || []);

  // Load data on component mount
  useEffect(() => {
    loadOrganizationNames();
  }, [loadOrganizationNames]);

  // Set default organization when organizations are loaded
  useEffect(() => {
    if (availableOrganizations.length > 0 && !selectedOrganization) {
      const defaultOrg = availableOrganizations[0];
      setSelectedOrganization(defaultOrg);
      loadCachedData(defaultOrg);
      
      // Set default date range to last 30 days
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      setEndDate(today.toISOString().split('T')[0]);
      setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    }
  }, [availableOrganizations, selectedOrganization, loadCachedData]);

  if (!availableOrganizations || availableOrganizations.length === 0) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">No Organizations Available</h2>
        <p className="text-gray-600">You need to be a member of an organization to view reporting data.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Organization Selector */}
      <div className="mb-6">
        <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
          Select Organization
        </label>
        <select
          id="organization"
          value={selectedOrganization}
          onChange={(e) => handleOrganizationChange(e.target.value)}
          className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          {availableOrganizations.map((orgId) => (
            <option key={orgId} value={orgId}>
              {organizationNames[orgId] || orgId}
            </option>
          ))}
        </select>
      </div>

      {/* Refresh Button */}
      <div className="mb-6">
        <button
          onClick={handleRefresh}
          disabled={refreshing || !selectedOrganization}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {refreshing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Refreshing...
            </>
          ) : (
            <>
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Data
            </>
          )}
        </button>
        <p className="mt-2 text-sm text-gray-600">
          Data is cached for your session. Click refresh to get the latest information.
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Data Display */}
      {overviewData && (
        <>
          {/* Lifetime Statistics */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Lifetime Stats</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Lessons Accessed */}
              <div className="text-center">
                <div className="mb-3">
                  <svg className="h-12 w-12 text-gray-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-3xl font-bold text-blue-900 mb-1">
                  {overviewData.lifetimeStats.lessonsAccessed.toLocaleString()}
                </div>
                <div className="text-sm text-gray-900">Lessons Accessed</div>
              </div>

              {/* Logins */}
              <div className="text-center">
                <div className="mb-3">
                  <svg className="h-12 w-12 text-gray-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                </div>
                <div className="text-3xl font-bold text-blue-900 mb-1">
                  {overviewData.lifetimeStats.logins.toLocaleString()}
                </div>
                <div className="text-sm text-gray-900">Logins</div>
              </div>

              {/* Prompts Accessed */}
              <div className="text-center">
                <div className="mb-3">
                  <svg className="h-12 w-12 text-gray-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-3xl font-bold text-blue-900 mb-1">
                  {overviewData.lifetimeStats.promptsAccessed.toLocaleString()}
                </div>
                <div className="text-sm text-gray-900">Prompts Accessed</div>
              </div>

              {/* Lifetime Users */}
              <div className="text-center">
                <div className="mb-3">
                  <svg className="h-12 w-12 text-gray-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="text-3xl font-bold text-blue-900 mb-1">
                  {overviewData.lifetimeStats.lifetimeUsers.toLocaleString()}
                </div>
                <div className="text-sm text-gray-900">Lifetime Users</div>
              </div>
            </div>
          </div>

          {/* Date Range Report Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Date Range</h2>
            
            <div className="flex flex-col sm:flex-row gap-4 items-end mb-6">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                  From
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                  to
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <button
                onClick={runDateReport}
                disabled={runningReport || !startDate || !endDate}
                className="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {runningReport ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Running...
                  </>
                ) : (
                  'Run Report'
                )}
              </button>
            </div>

            {/* Report Results */}
            {dateReportData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Content Interactions */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Content Interactions</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">Data</th>
                          <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">Accessed</th>
                          <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 px-2 text-sm text-gray-900">Courses</td>
                          <td className="py-2 px-2 text-sm text-gray-900">{dateReportData.courses.accessed}</td>
                          <td className="py-2 px-2 text-sm text-gray-900">{dateReportData.courses.completed}</td>
                        </tr>

                        <tr className="border-b border-gray-100">
                          <td className="py-2 px-2 text-sm text-gray-900">Lessons</td>
                          <td className="py-2 px-2 text-sm text-gray-900">{dateReportData.lessons.accessed}</td>
                          <td className="py-2 px-2 text-sm text-gray-900">{dateReportData.lessons.completed}</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 px-2 text-sm text-gray-900">Tasks</td>
                          <td className="py-2 px-2 text-sm text-gray-900">n/a</td>
                          <td className="py-2 px-2 text-sm text-gray-900">{dateReportData.tasks.completed}</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 px-2 text-sm text-gray-900">Prompts</td>
                          <td className="py-2 px-2 text-sm text-gray-900">{dateReportData.prompts.accessed}</td>
                          <td className="py-2 px-2 text-sm text-gray-900">{dateReportData.prompts.completed}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 text-2xl font-bold text-green-600">
                    Total: {getContentTotal()}
                  </div>
                </div>

                {/* Human Interactions */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Human Interactions</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">Data</th>
                          <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 px-2 text-sm text-gray-900">New Users</td>
                          <td className="py-2 px-2 text-sm text-gray-900">{dateReportData.newUsers}</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 px-2 text-sm text-gray-900">Logins</td>
                          <td className="py-2 px-2 text-sm text-gray-900">{dateReportData.logins}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 text-2xl font-bold text-green-600">
                    Total: {getHumanTotal()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportingDashboard;
